import { useCallback, useEffect, useMemo } from "react";
import { FilterType } from "@/components/tables/types";
import {
  useDatatableContext,
  FilterConfig,
} from "@/components/tables/contexts/DatatableContextProvider";
import {
  RESOURCE_TYPE_OPTIONS,
  RESOURCE_TYPE_OPTIONS_DATABRICKS,
  RESOURCE_TYPE_OPTIONS_FREEMIUM,
} from "../../../constants";
import {
  getOpportunitiesResourceNameFilters,
  getOpportunitiesUserFilters,
  getOpportunitiesWarehouseFilters,
} from "@/helpers/apis";
import { isFreeStack } from "@/helpers/tenant";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useWorkspaces } from "@/pages/workspaces/hooks";
import { GetOpportunitiesDynamicFilterResponse } from "@/helpers/types";

/**
 * Registers filter-only filters (resource_type, resource_name, user, warehouse,
 * plus a Workspace chip on Databricks tenants) with DatatableContext via
 * registerFilters, so they appear in "More Filters" without needing hidden
 * table columns.
 *
 * The Workspace chip mirrors the SQL Warehouses page pattern
 * (`useWarehouseColumns.tsx`): a multi-select DROPDOWN sourced from the
 * `useWorkspaces()` hook that writes to the `?workspaceId=` URL param.
 * `useDiscoverFilters` reads the URL key and forwards as `workspace_id` on
 * the API request body; the BE's `GetOpportunitiesDataRequest` accepts it
 * and the `OpportunitiesRepository` applies it as a row-level predicate
 * via `_build_explicit_workspace_filter`. Workspace is intentionally a
 * separate concept from the Instance filter — they're distinct URL keys
 * mapped to different RBAC dimensions on the BE.
 */
export function useRegisterOpportunityFilters(): void {
  const { registerFilters } = useDatatableContext();
  const { isDatabricksTenant } = useAppState();
  // ``useWorkspaces``'s ``enabled`` parameter defaults to ``true`` if you
  // pass ``undefined``. ``isDatabricksTenant`` is typed as optional —
  // before the cast, a tenant with the flag absent would (incorrectly)
  // trigger the ``/cost/workspaces`` fetch. Cast to a real boolean so
  // the gate is unambiguous.
  const { data: workspacesList } = useWorkspaces(!!isDatabricksTenant);
  const { searchParams } = useFiltersBySearchParams();
  // Read the chip's current selection at registration time so typeahead
  // candidates scope to the same workspaces the user picked — otherwise
  // the chip narrows the opportunities list but the More-Filters
  // typeahead suggestions stay tenant-global (a confusing UX where you
  // can type a workspace's resource name and get "no matches" because
  // the global candidate set never includes it).
  const workspaceIdSelection = useMemo(() => searchParams.getAll("workspaceId"), [searchParams]);
  const workspaceOptions = useMemo(
    () =>
      (workspacesList?.workspaces ?? []).map(ws => ({
        label: ws.workspace_name || ws.workspace_id,
        value: ws.workspace_id,
      })),
    [workspacesList?.workspaces]
  );

  // The three opportunity-typeahead chips (resource_name, user, warehouse)
  // all consume the same paginated `{ items: [{ label, value }] }` API shape
  // and only differ by which API function they call. Factor the shared
  // config out so a new typeahead chip is one line, not 15.
  //
  // Every typeahead also forwards the active Workspace-chip selection as
  // `workspace_id=[...]` so candidates scope to the same workspaces the
  // user picked. The BE endpoints accept the param uniformly via the
  // /opportunities/filters/* routes.
  //
  // ``id`` includes the active workspace selection so that
  // ``APIFilterDropdown``'s React Query key (``[id, search, page, size]``)
  // changes whenever the chip changes — without that, the closure value
  // updates but the cache key stays stable, so the dropdown keeps
  // returning stale candidates from the previous workspace selection.
  // Sort the ids first so order-only differences ([a,b] vs [b,a]) don't
  // fragment the cache.
  const workspaceIdSuffix = useMemo(
    () =>
      workspaceIdSelection.length > 0 ? `:ws=${[...workspaceIdSelection].sort().join(",")}` : "",
    [workspaceIdSelection]
  );
  const apiTypeaheadConfig = useCallback(
    (
      label: string,
      searchKey: string,
      apiFn: (params: {
        prefix?: string;
        page?: number;
        workspace_id?: string[];
      }) => Promise<unknown>
    ): FilterConfig => ({
      // Unique per chip so the three typeaheads don't collide on the
      // shared ``"api-filter"`` fallback inside ``APIFilterDropdown``.
      id: `discover-typeahead-${searchKey}${workspaceIdSuffix}`,
      label,
      searchKey,
      filterType: FilterType.API_DROPDOWN,
      isArray: true,
      apiFunction: (params: unknown) => {
        const typedParams = params as { prefix?: string; page?: number };
        return apiFn({
          ...typedParams,
          workspace_id: workspaceIdSelection.length > 0 ? workspaceIdSelection : undefined,
        });
      },
      extractorFunction: (data: unknown) => {
        const typedData = data as GetOpportunitiesDynamicFilterResponse;
        return typedData?.items?.map(item => ({ label: item.label, value: item.value })) || [];
      },
      seeMore: true,
      pageSize: 20,
    }),
    [workspaceIdSelection, workspaceIdSuffix]
  );

  const filterConfigs = useMemo(() => {
    const resourceTypeOptions = isDatabricksTenant
      ? RESOURCE_TYPE_OPTIONS_DATABRICKS
      : isFreeStack()
        ? RESOURCE_TYPE_OPTIONS_FREEMIUM
        : RESOURCE_TYPE_OPTIONS;

    const configs: Record<string, FilterConfig> = {
      resource_type: {
        label: "Resource Type",
        searchKey: "resource_type",
        filterType: FilterType.DROPDOWN,
        isArray: true,
        options: resourceTypeOptions,
      },
      resource_name: apiTypeaheadConfig(
        "Resource Name",
        "resource_name",
        getOpportunitiesResourceNameFilters
      ),
      user: apiTypeaheadConfig("User", "user", getOpportunitiesUserFilters),
    };

    // The warehouse typeahead lists Snowflake warehouse names; the BE
    // returns an empty set on Databricks tenants (warehouse_name is a
    // Snowflake-shaped concept). Gate the chip off there so users don't
    // see an empty dropdown.
    if (!isFreeStack() && !isDatabricksTenant) {
      configs.warehouse = apiTypeaheadConfig(
        "Warehouse",
        "warehouse",
        getOpportunitiesWarehouseFilters
      );
    }

    if (isDatabricksTenant) {
      configs.workspaceId = {
        label: "Workspace",
        searchKey: "workspaceId",
        filterType: FilterType.DROPDOWN,
        isArray: true,
        options: workspaceOptions,
      };
    }

    return configs;
  }, [isDatabricksTenant, workspaceOptions, apiTypeaheadConfig]);

  useEffect(() => {
    registerFilters(filterConfigs);
  }, [filterConfigs, registerFilters]);
}
